import { Suspense, useMemo } from 'react';
import { Link } from 'react-router-dom';
import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';
import { useArticlesAndPodcastsSection } from '../../hooks/useArticlesAndPodcastsSection';
import { useHome } from '../../hooks/useHome';
import { ArticlesAndPodcastsSectionSkeleton } from './ArticlesAndPodcastsSectionSkeleton';
import Button from '@/components/Button';
import { CustomImage } from '@/components/CustomImage';

interface ArticleCardProps {
  title: string;
  description?: string | null;
  imageUrl?: string;
  imageAlt?: string;
  primaryButtonText?: string | null;
  primaryButtonLink?: string;
  secondaryButtonText?: string | null;
  secondaryButtonLink?: string;
}

const ArticleCard = ({
  title,
  description,
  imageUrl,
  imageAlt,
  primaryButtonText,
  primaryButtonLink,
  secondaryButtonText,
  secondaryButtonLink,
}: ArticleCardProps) => {
  return (
    <div className="flex flex-col rounded-[14px] border-2 border-[#d0e2df] bg-white p-4">
      {imageUrl && (
        <div className="mb-4  w-full overflow-hidden rounded-sh-16 bg-[#f0f7f6]">
          <CustomImage src={imageUrl} alt={imageAlt || title} />
        </div>
      )}

      <div className="flex flex-col gap-2">
        <h3 className="mb-0 font-haffer text-sh-14-20 font-bold text-black">{title}</h3>

        {description && <p className="text-sh-16-24 text-black">{description}</p>}

        <div className="flex items-center gap-4 pt-4">
          {primaryButtonText && primaryButtonLink && (
            <Link to={primaryButtonLink}>
              <Button theme="outline-accent">{primaryButtonText}</Button>
            </Link>
          )}

          {secondaryButtonText && secondaryButtonLink && (
            <Link
              to={secondaryButtonLink}
              className="font-haffer text-sh-14-20 font-bold text-dark-green hover:underline">
              {secondaryButtonText}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

const ArticlesAndPodcastsSectionComponent = () => {
  const { homePage } = useHome();
  const { latestKnowledgeBase, latestPodcast } = useArticlesAndPodcastsSection();

  const articlesAndPodcastsSection = useMemo(
    () =>
      homePage?.homePage?.articlesAndPodcastsSection as
        | {
            title?: string | null;
            description?: string | null;
            readNowArticle?: string | null;
            viewAllArticle?: string | null;
            listenNowPodcast?: string | null;
            viewAllPodcasts?: string | null;
            icon?: {
              url: string;
            };
          }
        | undefined,
    [homePage?.homePage?.articlesAndPodcastsSection],
  );

  if (!latestKnowledgeBase && !latestPodcast) {
    return null;
  }

  return (
    <AnimatedFadeInComponent fromBottom>
      <div>
        <div className="mb-sh-15 flex items-center gap-4">
          <img src={articlesAndPodcastsSection?.icon?.url || ''} alt="Icon" className="size-6" />
          <h2 className="font-haffer text-sh-28-32 font-bold">{articlesAndPodcastsSection?.title || 'Key dates'}</h2>
        </div>
        {articlesAndPodcastsSection?.description && (
          <p className="mb-sh-30 font-haffer text-sh-16-24 text-dark-grey">{articlesAndPodcastsSection.description}</p>
        )}

        <div className="grid grid-cols-1 gap-sh-30 lg:grid-cols-2">
          {latestKnowledgeBase && (
            <ArticleCard
              title={latestKnowledgeBase.title || ''}
              description={latestKnowledgeBase.briefDescription}
              imageUrl={latestKnowledgeBase.cover?.url}
              imageAlt={latestKnowledgeBase.cover?.alternativeText || latestKnowledgeBase.title || ''}
              primaryButtonText={articlesAndPodcastsSection?.readNowArticle}
              primaryButtonLink={latestKnowledgeBase.slug ? `/knowledge-base/${latestKnowledgeBase.slug}` : undefined}
              secondaryButtonText={articlesAndPodcastsSection?.viewAllArticle}
              secondaryButtonLink="/knowledge-base"
            />
          )}

          {latestPodcast && (
            <ArticleCard
              title={latestPodcast.title || ''}
              description={latestPodcast.briefDescription}
              imageUrl={latestPodcast.cover?.url}
              imageAlt={latestPodcast.cover?.alternativeText || latestPodcast.title || ''}
              primaryButtonText={articlesAndPodcastsSection?.listenNowPodcast}
              primaryButtonLink={latestPodcast.slug ? `/podcasts/${latestPodcast.slug}` : undefined}
              secondaryButtonText={articlesAndPodcastsSection?.viewAllPodcasts}
              secondaryButtonLink="/podcasts"
            />
          )}
        </div>
      </div>
    </AnimatedFadeInComponent>
  );
};

export const ArticlesAndPodcastsSection = () => {
  return (
    <Suspense fallback={<ArticlesAndPodcastsSectionSkeleton />}>
      <ArticlesAndPodcastsSectionComponent />
    </Suspense>
  );
};
